import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { CountrySelect } from '../ui/country-select';
import { InternationalPhoneInput } from '../ui/international-phone-input';
import { CONTACT_METHOD_OPTIONS, INSTRUMENT_OPTIONS } from '@/models/lead.ts';
import { useActiveLeadStatuses } from '../../api/lead-statuses/use-lead-statuses';
import { useUsers } from '@/api/users/use-users.ts';
import { useCustomAttributeDefinitionsManagement } from '@/hooks/useCustomAttributes.ts';
import DynamicInput from '../ui/DynamicInput';
import { normalizeCustomAttributesForForm } from '@/utils/customAttributes';
import { useSystemConfiguration } from '@/api/system-configuration/use-system-configuration';
import { utcIsoToDatetimeLocal } from '@/utils/formatters';
import { PHONE_REGEX, PHONE_VALIDATION_MESSAGE } from '@/utils/phoneValidation';

const leadSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    full_name: z.string().optional(),
    email: z.string().email('Invalid email address'),
    phone: z
        .string()
        .min(1, 'Phone number is required')
        .regex(PHONE_REGEX, PHONE_VALIDATION_MESSAGE),
    additional_phone_number: z
        .string()
        .regex(PHONE_REGEX, PHONE_VALIDATION_MESSAGE)
        .optional()
        .or(z.literal('')),
    country: z.string().optional(),
    preferred_language: z.string().optional(),
    date_of_birth: z.string().optional(),
    status: z.string().optional(),
    lead_status: z.string().min(1, 'Lead status is required'),
    marketing_campaign_id: z.string().optional(),
    sales_team: z.string().optional(),
    ib_referral_id: z.string().optional(),
    contact_attempts: z.number().min(0).optional(),
    last_contact_date: z.string().optional(),
    next_follow_up_date: z.string().optional(),
    preferred_contact_method: z.string().optional(),
    instruments_of_interest: z.array(z.string()).optional(),
    time_zone: z.string().optional(),
    regulatory_region: z.string().optional(),
    custom_fields: z.record(z.any()).optional(),
    custom_attributes: z.record(z.any()).optional(),
    notes: z.string().optional(),
    assigned_user_id: z.string().optional(),
});

type LeadFormData = z.infer<typeof leadSchema>;

interface LeadFormProps {
    initialData?: Partial<LeadFormData>;
    onSubmit: (data: LeadFormData) => void;
    onCancel: () => void;
    isLoading?: boolean;
    mode?: 'create' | 'edit';
    /** API validation errors (e.g. from 422) to show under fields like lead_status */
    serverErrors?: Record<string, string[]>;
}

const LeadForm: React.FC<LeadFormProps> = ({
    initialData,
    onSubmit,
    onCancel,
    isLoading = false,
    mode = 'create',
    serverErrors,
}) => {
    const [selectedInstruments, setSelectedInstruments] = useState<string[]>(
        initialData?.instruments_of_interest || []
    );

    // Fetch dynamic lead statuses
    const { data: leadStatusesData } = useActiveLeadStatuses();
    const leadStatuses = leadStatusesData?.data || [];

    // Fetch active users for assignee dropdown
    const { data: usersResponse } = useUsers({ status: 'active', per_page: 100 });
    const users = usersResponse?.data || [];

    // Fetch custom attribute definitions
    const { definitions: customAttributeDefinitions, isLoading: isLoadingCustomAttributes } =
        useCustomAttributeDefinitionsManagement();

    const { data: systemConfig } = useSystemConfiguration();
    const crmTimezone = systemConfig?.timezone ?? 'UTC';
    // API returns datetimes in UTC (ISO with Z); convert to datetime-local in tenant TZ for inputs
    const lastContactLocal = utcIsoToDatetimeLocal(initialData?.last_contact_date, crmTimezone);
    const nextFollowUpLocal = utcIsoToDatetimeLocal(initialData?.next_follow_up_date, crmTimezone);

    const {
        control,
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm<LeadFormData>({
        resolver: zodResolver(leadSchema),
        defaultValues: {
            name: initialData?.name || '',
            email: initialData?.email || '',
            preferred_language: initialData?.preferred_language || 'en',
            status: initialData?.status || 'new',
            contact_attempts: initialData?.contact_attempts || 0,
            preferred_contact_method: initialData?.preferred_contact_method || 'email',
            instruments_of_interest: initialData?.instruments_of_interest || [],
            full_name: initialData?.full_name || '',
            phone: initialData?.phone || '',
            additional_phone_number: initialData?.additional_phone_number || '',
            country: initialData?.country || '',
            date_of_birth: initialData?.date_of_birth || '',
            marketing_campaign_id: initialData?.marketing_campaign_id || '',
            sales_team: initialData?.sales_team || '',
            ib_referral_id: initialData?.ib_referral_id || '',
            last_contact_date: lastContactLocal,
            next_follow_up_date: nextFollowUpLocal,
            time_zone: initialData?.time_zone || '',
            regulatory_region: initialData?.regulatory_region || '',
            custom_attributes:
                normalizeCustomAttributesForForm(initialData?.custom_attributes) || {},
            notes: Array.isArray(initialData?.notes)
                ? initialData.notes.join('\n')
                : initialData?.notes || '',
            assigned_user_id: initialData?.assigned_user_id || '',
        },
    });

    const handleInstrumentToggle = (instrument: string) => {
        const currentInstruments = selectedInstruments;
        const newInstruments = currentInstruments.includes(instrument)
            ? currentInstruments.filter(i => i !== instrument)
            : [...currentInstruments, instrument];

        setSelectedInstruments(newInstruments);
        setValue('instruments_of_interest', newInstruments);
    };

    const onSubmitHandler = (data: LeadFormData) => {
        const processedData = {
            ...data,
            assigned_user_id:
                !data.assigned_user_id || data.assigned_user_id === '__unassigned__'
                    ? undefined
                    : data.assigned_user_id,
        };
        onSubmit(processedData);
    };

    return (
        <form onSubmit={handleSubmit(onSubmitHandler)} className="space-y-6">
            <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-5">
                    <TabsTrigger value="basic">Basic Info</TabsTrigger>
                    <TabsTrigger value="contact">Contact & Follow-up</TabsTrigger>
                    <TabsTrigger value="trading">Trading Info</TabsTrigger>
                    <TabsTrigger value="advanced">Advanced</TabsTrigger>
                    <TabsTrigger value="custom">Custom Attributes</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Basic Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Name *</Label>
                                    <Controller
                                        name="name"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                placeholder="Enter name"
                                                className={errors.name ? 'border-red-500' : ''}
                                            />
                                        )}
                                    />
                                    {errors.name && (
                                        <p className="text-sm text-red-500">
                                            {errors.name.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="full_name">Full Name</Label>
                                    <Controller
                                        name="full_name"
                                        control={control}
                                        render={({ field }) => (
                                            <Input {...field} placeholder="Enter full name" />
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email *</Label>
                                    <Controller
                                        name="email"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                type="email"
                                                placeholder="Enter email"
                                                className={errors.email ? 'border-red-500' : ''}
                                            />
                                        )}
                                    />
                                    {errors.email && (
                                        <p className="text-sm text-red-500">
                                            {errors.email.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="phone">Phone *</Label>
                                    <Controller
                                        name="phone"
                                        control={control}
                                        render={({ field }) => (
                                            <InternationalPhoneInput
                                                value={field.value}
                                                onChange={field.onChange}
                                                placeholder="Enter phone number"
                                                className={errors.phone ? 'border-red-500' : ''}
                                            />
                                        )}
                                    />
                                    {errors.phone && (
                                        <p className="text-sm text-red-500">
                                            {errors.phone.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="additional_phone_number">
                                        Additional Phone Number
                                    </Label>
                                    <Controller
                                        name="additional_phone_number"
                                        control={control}
                                        render={({ field }) => (
                                            <InternationalPhoneInput
                                                value={field.value}
                                                onChange={field.onChange}
                                                placeholder="Enter additional phone number"
                                                className={
                                                    errors.additional_phone_number
                                                        ? 'border-red-500'
                                                        : ''
                                                }
                                            />
                                        )}
                                    />
                                    {errors.additional_phone_number && (
                                        <p className="text-sm text-red-500">
                                            {errors.additional_phone_number.message}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="country">Country</Label>
                                    <Controller
                                        name="country"
                                        control={control}
                                        render={({ field }) => (
                                            <CountrySelect
                                                value={field.value}
                                                onChange={field.onChange}
                                                placeholder="Select country"
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="date_of_birth">Date of Birth</Label>
                                    <Controller
                                        name="date_of_birth"
                                        control={control}
                                        render={({ field }) => <Input {...field} type="date" />}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="preferred_language">Preferred Language</Label>
                                    <Controller
                                        name="preferred_language"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select language" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="en">English</SelectItem>
                                                    <SelectItem value="ar">Arabic</SelectItem>
                                                    <SelectItem value="es">Spanish</SelectItem>
                                                    <SelectItem value="fr">French</SelectItem>
                                                    <SelectItem value="de">German</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="lead_status">Lead Status *</Label>
                                    <Controller
                                        name="lead_status"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <SelectTrigger
                                                    className={
                                                        errors.lead_status ? 'border-red-500' : ''
                                                    }
                                                >
                                                    <SelectValue placeholder="Select status" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {leadStatuses.map(status => (
                                                        <SelectItem
                                                            key={status.id}
                                                            value={status.id}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <div
                                                                    className="w-3 h-3 rounded-full border border-border"
                                                                    style={{
                                                                        backgroundColor:
                                                                            status.color,
                                                                    }}
                                                                />
                                                                {status.label}
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                    {errors.lead_status && (
                                        <p className="text-sm text-red-500">
                                            {errors.lead_status.message}
                                        </p>
                                    )}
                                    {serverErrors?.lead_status?.[0] && (
                                        <p className="text-sm text-red-500">
                                            {serverErrors.lead_status[0]}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="assigned_user_id">Assigned To</Label>
                                    <Controller
                                        name="assigned_user_id"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select assignee" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="__unassigned__">
                                                        Unassigned
                                                    </SelectItem>
                                                    {users.map(user => (
                                                        <SelectItem key={user.id} value={user.id}>
                                                            {user.name}{' '}
                                                            {user.title && `(${user.title})`}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="contact" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Contact & Follow-up</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="assigned_user_id">Assigned To</Label>
                                    <Controller
                                        name="assigned_user_id"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select assignee" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="__unassigned__">
                                                        Unassigned
                                                    </SelectItem>
                                                    {users.map(user => (
                                                        <SelectItem key={user.id} value={user.id}>
                                                            {user.name}{' '}
                                                            {user.title && `(${user.title})`}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="preferred_contact_method">
                                        Preferred Contact Method
                                    </Label>
                                    <Controller
                                        name="preferred_contact_method"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select method" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {CONTACT_METHOD_OPTIONS.map(option => (
                                                        <SelectItem
                                                            key={option.value}
                                                            value={option.value}
                                                        >
                                                            {option.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="contact_attempts">Contact Attempts</Label>
                                    <Controller
                                        name="contact_attempts"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                type="number"
                                                min="0"
                                                onChange={e =>
                                                    field.onChange(parseInt(e.target.value) || 0)
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="last_contact_date">Last Contact Date</Label>
                                    <Controller
                                        name="last_contact_date"
                                        control={control}
                                        render={({ field }) => (
                                            <Input {...field} type="datetime-local" />
                                        )}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="next_follow_up_date">Next Follow-up Date</Label>
                                    <Controller
                                        name="next_follow_up_date"
                                        control={control}
                                        render={({ field }) => (
                                            <Input {...field} type="datetime-local" />
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="notes">Notes</Label>
                                <Controller
                                    name="notes"
                                    control={control}
                                    render={({ field }) => (
                                        <Textarea
                                            {...field}
                                            placeholder="Enter notes about this lead"
                                            rows={3}
                                        />
                                    )}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="trading" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Trading Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label>Instruments of Interest</Label>
                                <div className="grid grid-cols-3 gap-2">
                                    {INSTRUMENT_OPTIONS.map(instrument => (
                                        <div
                                            key={instrument.value}
                                            className="flex items-center space-x-2"
                                        >
                                            <Checkbox
                                                id={instrument.value}
                                                checked={selectedInstruments.includes(
                                                    instrument.value
                                                )}
                                                onCheckedChange={() =>
                                                    handleInstrumentToggle(instrument.value)
                                                }
                                            />
                                            <Label htmlFor={instrument.value} className="text-sm">
                                                {instrument.label}
                                            </Label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="time_zone">Time Zone</Label>
                                    <Controller
                                        name="time_zone"
                                        control={control}
                                        render={({ field }) => (
                                            <Input {...field} placeholder="e.g., UTC+3" />
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="regulatory_region">Regulatory Region</Label>
                                <Controller
                                    name="regulatory_region"
                                    control={control}
                                    render={({ field }) => (
                                        <Input {...field} placeholder="e.g., EU, US, UK" />
                                    )}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="advanced" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Advanced Settings</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="marketing_campaign_id">
                                        Marketing Campaign ID
                                    </Label>
                                    <Controller
                                        name="marketing_campaign_id"
                                        control={control}
                                        render={({ field }) => (
                                            <Input {...field} placeholder="Enter campaign ID" />
                                        )}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="sales_team">Sales Team</Label>
                                    <Controller
                                        name="sales_team"
                                        control={control}
                                        render={({ field }) => (
                                            <Input {...field} placeholder="Enter sales team" />
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="ib_referral_id">IB Referral ID</Label>
                                <Controller
                                    name="ib_referral_id"
                                    control={control}
                                    render={({ field }) => (
                                        <Input {...field} placeholder="Enter IB referral ID" />
                                    )}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="custom" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Custom Attributes</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {isLoadingCustomAttributes ? (
                                <div className="text-center py-4">
                                    <p className="text-muted-foreground">
                                        Loading custom attributes...
                                    </p>
                                </div>
                            ) : customAttributeDefinitions.length === 0 ? (
                                <div className="text-center py-4">
                                    <p className="text-muted-foreground">
                                        No custom attributes defined yet.
                                    </p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Contact your administrator to set up custom attributes.
                                    </p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {customAttributeDefinitions.map(definition => (
                                        <Controller
                                            key={definition.id}
                                            name={`custom_attributes.${definition.name}`}
                                            control={control}
                                            render={({ field }) => (
                                                <DynamicInput
                                                    definition={definition}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    mode="form"
                                                    placeholder={`Enter ${definition.label.toLowerCase()}`}
                                                />
                                            )}
                                        />
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                    {isLoading ? 'Saving...' : mode === 'create' ? 'Create Lead' : 'Update Lead'}
                </Button>
            </div>
        </form>
    );
};

export default LeadForm;
